import { useState, type ReactNode } from 'react';
import { Card } from '../ui/Card';
import { startCheckout } from '../../lib/subscriptionService';

interface PaywallModalProps {
  onClose: () => void;
  /** Which book the user was trying to access */
  bookNumber?: number;
}

export function PaywallModal({ onClose, bookNumber }: PaywallModalProps) {
  const [loading, setLoading] = useState<'monthly' | 'annual' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCheckout = async (plan: 'monthly' | 'annual') => {
    setLoading(plan);
    setError(null);
    try {
      await startCheckout(plan);
    } catch {
      setError('Unable to start checkout. Please try again.');
      setLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card
        className="max-w-md w-full p-0 overflow-hidden"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-primary-dark)] p-6 text-white text-center relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <h2 className="font-display text-xl font-semibold mb-2">
            {bookNumber
              ? `Book ${bookNumber} is a Premium feature`
              : 'Unlock the full curriculum'}
          </h2>
          <p className="text-white/85 text-sm">
            Books 2-3, advanced exercises, Quranic Arabic, and more.
          </p>
        </div>

        {/* Pricing */}
        <div className="p-6 space-y-3">
          {/* Annual — highlighted */}
          <button
            onClick={() => handleCheckout('annual')}
            disabled={loading !== null}
            className="w-full p-4 rounded-[var(--radius-lg)] border-2 border-[var(--color-primary)] bg-[var(--color-primary)]/5 hover:bg-[var(--color-primary)]/10 transition-colors text-left relative"
          >
            <span className="absolute -top-2.5 left-4 px-2 py-0.5 bg-[var(--color-primary)] text-white text-xs font-semibold rounded">
              Best value
            </span>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-[var(--color-ink)]">Annual</p>
                <p className="text-sm text-[var(--color-ink-muted)]">£6.67/month, billed yearly</p>
              </div>
              <div className="text-right">
                <p className="font-display text-xl font-bold text-[var(--color-primary)]">£80</p>
                <p className="text-xs text-[var(--color-ink-muted)]">per year</p>
              </div>
            </div>
            {loading === 'annual' && (
              <div className="absolute inset-0 bg-white/60 dark:bg-black/40 flex items-center justify-center rounded-[var(--radius-lg)]">
                <div className="animate-spin h-5 w-5 border-2 border-[var(--color-primary)] border-t-transparent rounded-full" />
              </div>
            )}
          </button>

          {/* Monthly */}
          <button
            onClick={() => handleCheckout('monthly')}
            disabled={loading !== null}
            className="w-full p-4 rounded-[var(--radius-lg)] border-2 border-[var(--color-border)] hover:border-[var(--color-primary)] transition-colors text-left relative"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-[var(--color-ink)]">Monthly</p>
                <p className="text-sm text-[var(--color-ink-muted)]">Cancel anytime</p>
              </div>
              <div className="text-right">
                <p className="font-display text-xl font-bold text-[var(--color-ink)]">£10</p>
                <p className="text-xs text-[var(--color-ink-muted)]">per month</p>
              </div>
            </div>
            {loading === 'monthly' && (
              <div className="absolute inset-0 bg-white/60 dark:bg-black/40 flex items-center justify-center rounded-[var(--radius-lg)]">
                <div className="animate-spin h-5 w-5 border-2 border-[var(--color-primary)] border-t-transparent rounded-full" />
              </div>
            )}
          </button>

          {error && (
            <p className="text-sm text-[var(--color-error)] text-center">{error}</p>
          )}

          {/* Value props */}
          <div className="pt-3 space-y-2">
            <ValueProp icon="book" text="Books 2 & 3 — 184 lessons" />
            <ValueProp icon="pen" text="15+ active exercise types including i'rab" />
            <ValueProp icon="star" text="Quranic Arabic, stories, and more" />
            <ValueProp icon="shield" text="Your data stays on your device" />
          </div>

          {/* Scholarship link */}
          <p className="text-center text-sm text-[var(--color-ink-muted)] pt-2">
            Can't afford it?{' '}
            <a
              href="https://forms.gle/scholarship"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--color-primary)] underline hover:no-underline"
            >
              Apply for a scholarship
            </a>
          </p>
        </div>
      </Card>
    </div>
  );
}

function ValueProp({ icon, text }: { icon: string; text: string }) {
  const icons: Record<string, ReactNode> = {
    book: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
    pen: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
      </svg>
    ),
    star: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
      </svg>
    ),
    shield: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
  };

  return (
    <div className="flex items-center gap-2.5 text-sm text-[var(--color-ink-muted)]">
      <span className="text-[var(--color-primary)] flex-shrink-0">{icons[icon]}</span>
      {text}
    </div>
  );
}

export default PaywallModal;
